'use client';

import { SVGProps, CSSProperties } from 'react';
import BrandAmexSVG from '@/assets/images/brand/brand-amex.svg';
import BrandApplePaySVG from '@/assets/images/brand/brand-apple-pay.svg';
import BrandGooglePaySVG from '@/assets/images/brand/brand-google-pay.svg';
import BrandMastercardSVG from '@/assets/images/brand/brand-mastercard.svg';
import BrandPaypalSVG from '@/assets/images/brand/brand-paypal.svg';
import BrandStripeSVG from '@/assets/images/brand/brand-stripe.svg';
// Brand SVGs — static named imports (SVGR via @svgr/webpack)
// social-linkedin excluded: no self-hosted SVG asset
import BrandVisaSVG from '@/assets/images/brand/brand-visa.svg';
import SocialFacebookSVG from '@/assets/images/brand/social-facebook.svg';
import SocialInstagramSVG from '@/assets/images/brand/social-instagram.svg';
import SocialPinterestSVG from '@/assets/images/brand/social-pinterest.svg';
import SocialSpotifySVG from '@/assets/images/brand/social-spotify.svg';
import SocialTiktokSVG from '@/assets/images/brand/social-tiktok.svg';
import SocialXSVG from '@/assets/images/brand/social-x.svg';
import SocialYoutubeSVG from '@/assets/images/brand/social-youtube.svg';
// Original (pre-refactor) monochrome social SVGs — for the 'original' brand pack
import OriginalFacebookSVG from '@/assets/images/facebook.svg';
import OriginalInstagramSVG from '@/assets/images/instagram.svg';
import OriginalTiktokSVG from '@/assets/images/tiktok.svg';
import OriginalYoutubeSVG from '@/assets/images/youtube.svg';
import { getLogger } from '@/shared/lib/logger.client';
import { SIMPLE_ICONS_MAP } from './catalog/simple-icons-name-map';
import { CustomIcon } from './custom-icon';
import { useIconConfig, resolveIconPack, resolveBrandPack } from './icon-config';
import { ICON_MAP } from './icon-map.generated';
import type { IconName } from './icon-map.generated';

const logger = getLogger('Icon');

type BrandSVGComponent = React.ComponentType<SVGProps<SVGSVGElement>>;

// Only entries that have a corresponding self-hosted SVG asset
const BRAND_SVG_MAP: Record<string, BrandSVGComponent> = {
    'brand-visa': BrandVisaSVG as BrandSVGComponent,
    'brand-mastercard': BrandMastercardSVG as BrandSVGComponent,
    'brand-paypal': BrandPaypalSVG as BrandSVGComponent,
    'brand-amex': BrandAmexSVG as BrandSVGComponent,
    'brand-google-pay': BrandGooglePaySVG as BrandSVGComponent,
    'brand-apple-pay': BrandApplePaySVG as BrandSVGComponent,
    'brand-stripe': BrandStripeSVG as BrandSVGComponent,
    'social-facebook': SocialFacebookSVG as BrandSVGComponent,
    'social-instagram': SocialInstagramSVG as BrandSVGComponent,
    'social-youtube': SocialYoutubeSVG as BrandSVGComponent,
    'social-tiktok': SocialTiktokSVG as BrandSVGComponent,
    'social-x': SocialXSVG as BrandSVGComponent,
    'social-spotify': SocialSpotifySVG as BrandSVGComponent,
    'social-pinterest': SocialPinterestSVG as BrandSVGComponent,
};

// Original monochrome social icons — only platforms that had a pre-refactor SVG.
// These recolor via currentColor (unlike the official-color branded SVGs).
const ORIGINAL_SOCIAL_MAP: Record<string, BrandSVGComponent> = {
    'social-facebook': OriginalFacebookSVG as BrandSVGComponent,
    'social-instagram': OriginalInstagramSVG as BrandSVGComponent,
    'social-youtube': OriginalYoutubeSVG as BrandSVGComponent,
    'social-tiktok': OriginalTiktokSVG as BrandSVGComponent,
};

// Brand icon names that have an original (pre-refactor) SVG variant.
// Single source of truth — editor derives its capability set from this.
export const ORIGINAL_SOCIAL_ICON_NAMES = Object.keys(ORIGINAL_SOCIAL_MAP);

export type BrandIconName = keyof typeof BRAND_SVG_MAP;
export type AnyIconName = IconName | BrandIconName;

export type IconWeight = 'regular' | 'fill';

export type IconProps = SVGProps<SVGSVGElement> & {
    name: AnyIconName;
    size?: number;
    className?: string;
    weight?: IconWeight;
};

// Fallback slot: 'error' (CircleAlert) — closest circle in ICON_MAP
const FALLBACK_NAME: IconName = 'error';

export const Icon = ({ name, size = 24, className, weight = 'regular', ...svgProps }: IconProps) => {
    const config = useIconConfig();

    // Vlastní (uživatelem nahraná) ikona — nejvyšší priorita
    const custom = config.customIcons?.[name];
    if (custom?.svg && custom.svg.includes('<svg')) {
        return (
            <CustomIcon
                svg={custom.svg}
                size={size}
                className={className}
                style={svgProps.style as CSSProperties | undefined}
            />
        );
    }

    // Brand routing — self-hosted SVG, no color override (licensing)
    if (name in SIMPLE_ICONS_MAP) {
        // 'original' brand pack — pre-refactor monochrome social SVG (recolors via currentColor)
        if (resolveBrandPack(name, config) === 'original') {
            const OriginalSvg = ORIGINAL_SOCIAL_MAP[name];
            if (OriginalSvg) {
                return <OriginalSvg width={size} height={size} className={className} {...svgProps} />;
            }
            // No original SVG for this brand (e.g. social-x, payment brands) → branded below
        }

        const BrandComponent = BRAND_SVG_MAP[name];
        if (BrandComponent) {
            return <BrandComponent width={size} height={size} className={className} {...svgProps} />;
        }
        // Brand name exists in SIMPLE_ICONS_MAP but has no self-hosted SVG (e.g. social-linkedin)
        // Fall through to unknown-name fallback below
    }

    // Regular icon routing
    const slot = ICON_MAP[name as IconName];

    if (!slot) {
        if (process.env.NODE_ENV !== 'production') {
            logger.warn(`[Icon] Unknown icon name: "${name}". Rendering fallback '${FALLBACK_NAME}'.`);
        }
        const fallbackSlot = ICON_MAP[FALLBACK_NAME];
        const FallbackLucide = fallbackSlot.lucide;
        return <FallbackLucide size={size} className={className} {...svgProps} />;
    }

    let pack = resolveIconPack(name, config);

    // Default pack — původní self-hosted SVG; chybí-li, fallback na Phosphor
    if (pack === 'default') {
        // Future-proofing: slot přidaný dřív než jeho původní SVG nemá pole `default` → fallback na Phosphor níže.
        const DefaultSvg = 'default' in slot ? slot.default : undefined;
        if (DefaultSvg) {
            return <DefaultSvg width={size} height={size} className={className} {...svgProps} />;
        }
        pack = 'phosphor';
    }

    if (pack === 'phosphor') {
        const PhComponent = slot.phosphor;
        const phosphorWeight = weight === 'fill' ? 'fill' : (slot.phosphorWeight ?? 'regular');
        return <PhComponent size={size} weight={phosphorWeight} className={className} {...svgProps} />;
    }

    // Lucide pack
    const LucideComponent = slot.lucide;
    const isFillSlot = 'lucideFilled' in slot && slot.lucideFilled === true;
    const fillProp = isFillSlot || weight === 'fill' ? { fill: 'currentColor' } : {};

    return <LucideComponent size={size} className={className} {...fillProp} {...svgProps} />;
};
